import React, { useEffect, useState } from "react";
import { getUpcomingBookings, UpcomingBooking } from "../../services/dashboard";
import { Calendar, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/Button";
import GlobalLoader from "../ui/GlobalLoader";

interface UpcomingBookingsProps {
  currencySymbol?: string;
}

const UpcomingBookings: React.FC<UpcomingBookingsProps> = ({ currencySymbol = "£" }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [bookings, setBookings] = useState<UpcomingBooking[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBookings = async (date: Date) => {
    setLoading(true);
    try {
      const dateStr = date.toISOString().split("T")[0];
      const res = await getUpcomingBookings(dateStr);

      const formatted = res.bookings.map((b: any) => {
        // Extract staff name from the first item's employee
        const staffName = b.items?.[0]?.employee?.name ?? "No Staff Assigned";

        return {
          id: b.id,
          customer: b.user?.name ?? "Unknown",
          date: b.date_time?.split("T")[0] ?? "",
          time: b.date_time ? b.date_time.split("T")[1].substring(0, 5) : "",
          amount: b.amount_to_pay ?? 0,
          services_count: b.items?.length ?? 0,
          staff: staffName,
        };
      });

      setBookings(formatted);
    } catch (err) {
      console.error(err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(selectedDate);
  }, [selectedDate]);

  const daysToShow = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - selectedDate.getDay() + i);
    return d;
  });

  return (
    <div className="flex-1 bg-white rounded-2xl shadow-lg p-4 sm:p-6">
      <h2 className="text-xl font-bold text-[#1E1E1E] mb-4">
        Upcoming Bookings
      </h2>
      {/* Month Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 sm:gap-2">
          <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#885ABB]" />
          <p className="text-[#885ABB] font-bold text-base sm:text-lg">
            {selectedDate.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        <div className="flex ">
          <Button
            onClick={() =>
              setSelectedDate(new Date(selectedDate.getTime() - 7 * 86400000))
            }
            className="text-[#885ABB] transition-colors p-2"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>

          <Button
            onClick={() =>
              setSelectedDate(new Date(selectedDate.getTime() + 7 * 86400000))
            }
            className="text-gray-400 hover:text-[#885ABB] transition-colors p-2"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        </div>
      </div>

      {/* Day Selector */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-2 sm:pb-0 sm:overflow-visible scrollbar-hide">
        {daysToShow.map((day) => {
          const isSelected = day.toDateString() === selectedDate.toDateString();
          return (
            <button
              key={day.toDateString()}
              onClick={() => setSelectedDate(new Date(day))}
              className={`flex-none w-12 sm:flex-1 flex flex-col items-center justify-center py-3 px-1 sm:px-2 rounded-xl text-sm font-semibold transition-all duration-200 ${isSelected
                ? "bg-[#885ABB] text-white shadow-md scale-105"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
            >
              <span className="text-base mb-1">{day.getDate()}</span>
              <span className="text-[10px] sm:text-xs">
                {day.toLocaleDateString("en-US", { weekday: "short" })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <GlobalLoader fullScreen={false} description="Updating bookings..." compensateSidebar={false} />
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400 text-sm">No bookings for this date.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
          {bookings.map((b) => (
            <div
              key={b.id}
              className="bg-[#EDE3F7] p-4 rounded-xl hover:shadow-md transition-all duration-200 border-l-4 border-[#885ABB]"
            >
              {/* Customer + Amount */}
              <div className="flex justify-between items-start mb-2">
                <p className="font-bold text-[#1E1E1E] text-base uppercase tracking-wide">
                  {b.customer}
                </p>
                <p className="font-bold text-[#1E1E1E] text-lg">
                  {currencySymbol}{b.amount.toFixed(2)}
                </p>
              </div>

              {/* Date + Time */}
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-1 text-gray-700 text-sm">
                  <Calendar className="w-4 h-4 text-[#885ABB]" />
                  <span>{new Date(b.date).toLocaleDateString("en-GB")}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-700 text-sm">
                  <Clock className="w-4 h-4 text-[#885ABB]" />
                  <span>{b.time}</span>
                </div>
              </div>

              {/* Services Count */}
              <p className="text-sm font-semibold text-[#1E1E1E] mb-1">
                Services Included: {b.services_count}
              </p>

              {/* Staff Name */}
              {/* <p className="text-sm font-medium text-[#885ABB]">
                Staff: {b.staff || "Not Assigned"}
              </p> */}
            </div>
          ))}
        </div>
      )}

      {/* Scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #885ABB;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6B4799;
        }
      `}</style>
    </div>
  );
};

export default UpcomingBookings;
